import type { LDClient } from 'launchdarkly-react-client-sdk';
import type { User } from '@auth0/auth0-react';

import { LDMultiContext, LDScopeData } from './launch-darkly-types';

/**
 * Type guard to check if a context is a multi-context
 */
function isMultiContext(context: any): context is LDMultiContext {
  return context && context.kind === 'multi';
}

/**
 * Checks if the current LaunchDarkly context is anonymous
 */
export function isAnonymousContext(ldClient: LDClient | undefined): boolean {
  if (!ldClient) return true;

  try {
    const context = ldClient.getContext();
    if (!context) return true;

    // Check if it's a multi-context with user
    if (isMultiContext(context) && context.user) {
      return context.user.anonymous === true;
    }

    // For single user context, check if it's anonymous
    if (context && typeof context === 'object' && 'anonymous' in context) {
      return (context as any).anonymous === true;
    }

    return true;
  } catch (error) {
    console.error('[LaunchDarkly] Error checking context:', error);
    return true;
  }
}

/**
 * Builds a LaunchDarkly context from auth user and facility data
 * @returns A LaunchDarkly multi-context object or null if no user
 */
export function buildLDContextFromAuth(user: User | undefined, facilityData: LDScopeData): any {
  if (!user) return null;

  const context: LDMultiContext = { kind: 'multi' };
  let hasContext = false;

  // Add user context
  if (user.email) {
    context.user = {
      key: user.email,
      email: user.email,
      userId: user.sub,
      name: user.name || user.email,
    };
    hasContext = true;
  }

  // Add organization context
  if (facilityData.organizationId) {
    context.organization = { key: facilityData.organizationId };
    hasContext = true;
  }

  // Add facility context
  if (facilityData.facilityId) {
    context.facility = { key: facilityData.facilityId };
    hasContext = true;
  }

  // Add agent context
  if (facilityData.agentId) {
    context.agent = { key: facilityData.agentId };
    hasContext = true;
  }

  return hasContext ? context : null;
}

/**
 * Identifies user in LaunchDarkly with timeout
 * @param ldClient - The LaunchDarkly client instance
 * @param context - The context object to identify
 * @param timeoutMs - Timeout in milliseconds (default: 3000)
 * @returns Promise that resolves to true if identification succeeded, false otherwise
 */
export async function identifyWithTimeout(
  ldClient: LDClient,
  context: any,
  timeoutMs: number = 3000
): Promise<boolean> {
  try {
    const identifyPromise = ldClient.identify(context);
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Identification timeout')), timeoutMs)
    );

    await Promise.race([identifyPromise, timeoutPromise]);
    return true;
  } catch (error) {
    console.error('[LaunchDarkly] Identification failed or timed out:', error);
    return false;
  }
}
